export type Role = 'admin' | 'manager' | 'storekeeper' | 'viewer';
export type Platform = 'wb' | 'ozon';
export type OrderStatus = 'new' | 'reserved' | 'picking' | 'assembled' | 'shipped' | 'delivered' | 'cancelled';

export interface Warehouse { id: number; code: string; name: string; address: string | null; is_active: boolean }

export interface StockRow {
  storage_address?: string | null;
  product_id: number;
  warehouse_id: number;
  warehouse_code?: string;
  is_kit: boolean;
  physical: number;
  reserved: number | null;
  free: number;
  stock_buffer: number;
  to_marketplace: number;
  sku: string;
  name: string;
  unit?: string;
  is_active?: boolean;
  category_id?: number | null;
  category?: string | null;
  category_group_id?: number | null;
  category_group?: string | null;
  _changedAt?: number;
}

export interface Product {
  storage_address?: string | null;
  id: number;
  sku: string;
  article_1c?: string | null;
  /** код позиции в 1С — служебный, в списке не показывается */
  code_1c?: string | null;
  name: string;
  is_kit: boolean;
  unit: string;
  stock_buffer: number;
  safety_stock?: number;
  supplier_id?: number | null;
  lead_time_days?: number | null;
  purchase_price?: number | null;
  weight_g?: number | null;
  is_active: boolean;
  version: number;
  barcodes: any;
  components?: { component_id: number; sku: string; name: string; qty: number; image_url?: string | null }[];
  image_url?: string | null;
  in_kits?: { kit_id: number; sku: string; qty: number }[];
  mappings?: Mapping[];
  physical?: number | null;
  reserved?: number | null;
  free?: number | null;
  to_marketplace?: number | null;
}

export interface Mapping {
  id: number;
  product_id: number;
  account_id: number;
  account_name?: string;
  platform: Platform;
  mp_sku: string;
  mp_barcode: string | null;
  mp_offer_id: string | null;
  mp_chrt_id: string | null;
  is_active: boolean;
  sku?: string;
  name?: string;
  sync_pending?: boolean | null;
  dirty?: boolean | null;
  last_sent_qty?: number | null;
  last_sent_at?: string | null;
  sync_error?: string | null;
  last_error?: string | null;
}

export interface OrderRow {
  id: number;
  external_id: string;
  status: OrderStatus;
  warehouse_id: number;
  account_id: number;
  platform: Platform;
  account_name: string;
  pick_list_id: number | null;
  pick_list_number: string | null;
  ship_by: string | null;
  /** когда площадка приняла заказ */
  mp_created_at?: string | null;
  /** Ozon: отгрузить без просрочки до */
  ship_deadline?: string | null;
  /** Ozon: тариф отгрузки — текущий и следующий шаг */
  tariff?: {
    current_type: string | null; current_rate: number | null; current_charge: string | null;
    next_type: string | null; next_rate: number | null; next_charge: string | null; next_starts_at: string | null;
    currency: string | null;
  } | null;
  delivery_service?: string | null;
  mp_order_number?: string | null;
  mp_status?: string | null;
  mp_substatus?: string | null;
  created_at?: string;
  updated_at: string;
  items?: { product_id: number | null; sku: string; name: string | null; image_url?: string | null; category_id?: number | null; category?: string | null; category_group?: string | null; qty: number; price: number | null; unit?: string | null }[];
  unmapped: boolean;
}

export interface PickListRow {
  id: number;
  number: string;
  status: 'open' | 'in_progress' | 'done' | 'cancelled';
  warehouse_id: number;
  orders: number;
  orders_left: number;
  ship_by?: string | null;
  assigned_to_name?: string | null;
  created_at?: string;
  completed_at?: string | null;
}

export interface Account {
  id: number;
  platform: Platform;
  name: string;
  warehouse_id: number;
  warehouse_name: string;
  mp_warehouse_id?: string;
  client_id?: string | null;
  api_key_mask?: string;
  sync_enabled: boolean;
  batch_size?: number;
  orders_poll_sec?: number;
  settings?: Record<string, unknown>;
  sync_paused_until?: string | null;
  sync_last_run_at?: string | null;
  sync_last_status?: string | null;
  sync_last_error?: string | null;
  sync_pending?: number;
  orders_last_poll_at?: string | null;
  orders_last_ok_at?: string | null;
  orders_last_error?: string | null;
  orders_paused_until?: string | null;
}

export const ORDER_STATUS: Record<OrderStatus, { label: string; color: string }> = {
  new: { label: 'Новый', color: 'gray' },
  reserved: { label: 'Зарезервирован', color: 'blue' },
  picking: { label: 'В сборке', color: 'yellow' },
  assembled: { label: 'Собран', color: 'teal' },
  shipped: { label: 'Отгружен', color: 'green' },
  delivered: { label: 'Доставлен', color: 'green' },
  cancelled: { label: 'Отменён', color: 'red' },
};

export const PICK_STATUS = {
  open: { label: 'Открыт', color: 'blue' },
  in_progress: { label: 'Собирается', color: 'yellow' },
  done: { label: 'Собран', color: 'teal' },
  cancelled: { label: 'Отменён', color: 'red' },
} as const;

export const ROLE_LABEL: Record<Role, string> = { admin: 'Администратор', manager: 'Менеджер', storekeeper: 'Кладовщик', viewer: 'Просмотр' };
export const PLATFORM_LABEL: Record<Platform, string> = { wb: 'WB', ozon: 'Ozon' };
